/**
 * Session state. The access token never touches localStorage — it lives in the
 * `lib/api` module and dies with the tab. Persistence is the refresh token's
 * httpOnly cookie, which the browser sends on `POST /auth/refresh`.
 */
import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import {
  get,
  getAccessToken,
  isApiError,
  onUnauthenticated,
  post,
  refreshSession,
  setAccessToken,
} from '@/lib/api'
import {
  readAccessToken,
  type CurrentUser,
  type LoginChallenge,
  type Membership,
  type SessionPayload,
} from '@/lib/types'
import type { MembershipRole } from '@eua/shared'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<CurrentUser | null>(null)
  const memberships = ref<Membership[]>([])
  const currentTenantId = ref<string | null>(null)
  const bootstrapped = ref(false)
  const busy = ref(false)

  const isAuthenticated = computed(() => user.value !== null && getAccessToken() !== null)

  const currentMembership = computed(
    () => memberships.value.find((entry) => entry.id === currentTenantId.value) ?? null,
  )
  const currentTenant = computed(() => currentMembership.value)
  const role = computed<MembershipRole | null>(() => currentMembership.value?.role ?? null)
  const canEdit = computed(() => role.value === 'owner' || role.value === 'accountant')
  const isOwner = computed(() => role.value === 'owner')

  function applySession(payload: SessionPayload): void {
    setAccessToken(readAccessToken(payload))
    user.value = payload.user
    memberships.value = payload.memberships ?? []
    currentTenantId.value = payload.tenant?.id ?? memberships.value[0]?.id ?? currentTenantId.value
  }

  function clearSession(): void {
    setAccessToken(null)
    user.value = null
    memberships.value = []
    currentTenantId.value = null
  }

  /**
   * From a browser this account has signed in with before, the session comes
   * straight back. From an unknown one the API mails a code instead — the
   * caller shows the code step and finishes with `verifyLoginCode`.
   */
  async function login(email: string, password: string): Promise<'ok' | LoginChallenge> {
    busy.value = true
    try {
      const result = await post<SessionPayload | { challenge: LoginChallenge }>(
        '/auth/login',
        { email, password },
        { anonymous: true },
      )
      if ('challenge' in result) return result.challenge
      applySession(result)
      return 'ok'
    } finally {
      busy.value = false
    }
  }

  async function verifyLoginCode(challengeToken: string, code: string): Promise<void> {
    busy.value = true
    try {
      applySession(
        await post<SessionPayload>(
          '/auth/login/verify-code',
          { challengeToken, code },
          { anonymous: true },
        ),
      )
    } finally {
      busy.value = false
    }
  }

  async function register(input: {
    email: string
    password: string
    name: string
    businessName: string
    street: string
    zip: string
    city: string
  }): Promise<void> {
    busy.value = true
    try {
      applySession(await post<SessionPayload>('/auth/register', input, { anonymous: true }))
    } finally {
      busy.value = false
    }
  }

  async function acceptInvite(
    token: string,
    input: { name: string; password: string },
  ): Promise<void> {
    busy.value = true
    try {
      applySession(
        await post<SessionPayload>('/auth/accept-invite', { token, ...input }, { anonymous: true }),
      )
    } finally {
      busy.value = false
    }
  }

  async function logout(): Promise<void> {
    try {
      await post('/auth/logout', undefined)
    } catch {
      // A dead session still has to disappear from the UI.
    } finally {
      clearSession()
    }
  }

  /** Refreshes the access token and re-reads the session. */
  async function refresh(): Promise<boolean> {
    const token = await refreshSession()
    if (!token) {
      clearSession()
      return false
    }
    try {
      // The API exposes the session as GET /auth/me; the token comes from the
      // refresh call above and is not part of that payload.
      const session = await get<SessionPayload>('/auth/me', { noRetry: true })
      applySession({ ...session, accessToken: token })
      return true
    } catch (error) {
      // A refresh token without a readable session is unusable.
      if (isApiError(error) && error.status !== 0) clearSession()
      return false
    }
  }

  /** Runs before the router mounts; a missing cookie is the normal case. */
  async function bootstrap(): Promise<void> {
    if (bootstrapped.value) return
    try {
      await refresh()
    } finally {
      bootstrapped.value = true
    }
  }

  async function switchTenant(tenantId: string): Promise<void> {
    if (tenantId === currentTenantId.value) return
    busy.value = true
    try {
      applySession(await post<SessionPayload>('/auth/switch-tenant', { tenantId }))
      currentTenantId.value = tenantId
    } finally {
      busy.value = false
    }
  }

  /**
   * Records that the reader has seen the Neuerungen list up to `date`.
   *
   * The API only ever moves the marker forward, so a tab that was left open on
   * an older day cannot undo a newer visit — the answer it sends back is the
   * value that counts, and the local copy follows it.
   */
  async function markChangelogSeen(date: string): Promise<void> {
    const current = user.value
    if (!current) return
    if (current.changelogSeenDate !== null && current.changelogSeenDate >= date) return

    const result = await post<{ changelogSeenDate: string | null }>('/users/me/changelog-seen', {
      date,
    })
    if (user.value) user.value = { ...user.value, changelogSeenDate: result.changelogSeenDate }
  }

  // A refresh that fails mid-session must not leave a half-authenticated shell.
  onUnauthenticated(() => clearSession())

  return {
    user,
    memberships,
    currentTenantId,
    currentTenant,
    currentMembership,
    bootstrapped,
    busy,
    isAuthenticated,
    role,
    canEdit,
    isOwner,
    login,
    verifyLoginCode,
    register,
    acceptInvite,
    logout,
    refresh,
    bootstrap,
    switchTenant,
    markChangelogSeen,
    clearSession,
  }
})
